import { useEffect, useState, useMemo } from 'react';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

// ─── Format ───────────────────────────────────────────────────────────────────

function fmtUsd(n) {
  if (!n && n !== 0) return '$0';
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtNum(n) {
  return Number(n || 0).toLocaleString('tr-TR');
}

// ─── SVG Alan Grafiği ──────────────────────────────────────────────────────────

function AreaChart({ data, valueKey, color = '#6366f1', height = 140, formatTooltip }) {
  const [hover, setHover] = useState(null);
  const values = useMemo(() => data.map(d => parseFloat(d[valueKey]) || 0), [data, valueKey]);
  const max    = useMemo(() => Math.max(...values, 1), [values]);

  const W = 1000, H = height, PAD_T = 10, PAD_B = 6;

  const pts = values.map((v, i) => [
    values.length < 2 ? W / 2 : (i / (values.length - 1)) * W,
    PAD_T + (1 - v / max) * (H - PAD_T - PAD_B),
  ]);

  const linePts = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const areaPts = `${pts[0]?.[0] ?? 0},${H} ${linePts} ${pts.at(-1)?.[0] ?? W},${H}`;
  const gid     = `g-${valueKey}-${color.slice(1)}`;

  return (
    <div className="relative w-full select-none">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.22" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>
        <polygon points={areaPts} fill={`url(#${gid})`} />
        <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map(([x, y], i) => (
          <g key={i}>
            <rect
              x={x - W / (values.length * 2 + 2)} y={0}
              width={W / (values.length + 1)} height={H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
            {hover === i && (
              <>
                <line x1={x} y1={PAD_T} x2={x} y2={H} stroke={color} strokeWidth="1" strokeDasharray="4,3" opacity="0.4" />
                <circle cx={x} cy={y} r="5" fill="white" stroke={color} strokeWidth="2.5" />
              </>
            )}
          </g>
        ))}
      </svg>

      {hover !== null && data[hover] && (
        <div
          className="absolute top-1 pointer-events-none z-10"
          style={{
            left: `${(pts[hover][0] / W) * 100}%`,
            transform: pts[hover][0] > W * 0.7 ? 'translateX(-108%)' : 'translateX(10px)',
          }}
        >
          <div className="bg-gray-900 text-white text-xs rounded-xl px-3 py-2 shadow-xl whitespace-nowrap">
            <div className="font-semibold text-gray-200 mb-0.5">{data[hover].label}</div>
            <div className="text-white font-bold">{formatTooltip ? formatTooltip(values[hover]) : fmtNum(values[hover])}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function XLabels({ data, maxLabels = 8 }) {
  const step = Math.max(1, Math.floor(data.length / maxLabels));
  return (
    <div className="flex mt-1.5">
      {data.map((d, i) => (
        <div key={i} className="flex-1 text-center" style={{ minWidth: 0 }}>
          {(i % step === 0 || i === data.length - 1)
            ? <span className="text-[10px] text-gray-400 whitespace-nowrap">{d.label}</span>
            : null}
        </div>
      ))}
    </div>
  );
}

// ─── Aylık sütun grafiği — pixel yükseklik (güvenilir) ───────────────────────

const COL_H = 112; // px, h-28

function ColumnChart({ data, valueKey, color, formatVal }) {
  const values = data.map(d => parseFloat(d[valueKey]) || 0);
  const max    = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-2" style={{ height: COL_H }}>
      {data.map((d, i) => {
        const barH = Math.max((values[i] / max) * (COL_H - 22), 3);
        const isLast = i === data.length - 1;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
            <div
              className="w-full rounded-t-lg transition-all duration-500 relative group cursor-default"
              style={{ height: barH, backgroundColor: isLast ? color : color + '70' }}
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] rounded-lg px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                {formatVal ? formatVal(values[i]) : fmtNum(values[i])}
              </div>
            </div>
            <span className={`text-[10px] text-center leading-tight truncate w-full ${isLast ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>
              {d.month_short}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Haftanın günleri — pixel yükseklik ──────────────────────────────────────

const DOW_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const DOW_H  = 88; // px

function DayOfWeekChart({ data }) {
  const byDow = Object.fromEntries((data || []).map(d => [Number(d.dow), Number(d.order_count)]));
  const rows  = [1, 2, 3, 4, 5, 6, 0].map(dow => ({
    dow,
    label: DOW_TR[dow],
    count: byDow[dow] || 0,
  }));
  const max = Math.max(...rows.map(r => r.count), 1);

  return (
    <div className="flex items-end gap-2" style={{ height: DOW_H }}>
      {rows.map(r => {
        const barH  = Math.max((r.count / max) * (DOW_H - 20), 3);
        const isTop = r.count > 0 && r.count === max;
        return (
          <div key={r.dow} className="flex-1 flex flex-col items-center justify-end gap-1">
            <div
              className="w-full rounded-t-lg transition-all duration-500 relative group cursor-default"
              style={{ height: barH, backgroundColor: isTop ? '#6366f1' : r.count > 0 ? '#c7d2fe' : '#f3f4f6' }}
            >
              {r.count > 0 && (
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                  {r.count} sipariş
                </div>
              )}
            </div>
            <span className={`text-[10px] ${isTop ? 'text-indigo-600 font-bold' : 'text-gray-400'}`}>
              {r.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI kartı ────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon, accent, delta }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="text-xl">{icon}</div>
        {delta != null && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${delta >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold tracking-tight mb-1" style={{ color: accent }}>{value}</div>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Pipeline ─────────────────────────────────────────────────────────────────

const PIPELINE_CFG = {
  awaiting_packaging:    { label: 'Paket Bekliyor',  color: '#818cf8', bg: '#eef2ff' },
  awaiting_registration: { label: 'Kayıt Bekliyor',  color: '#fb923c', bg: '#fff7ed' },
  ready_to_ship:         { label: 'Sevkiyata Hazır', color: '#34d399', bg: '#ecfdf5' },
  awaiting_deliver:      { label: 'Teslim Bekliyor', color: '#fbbf24', bg: '#fffbeb' },
  delivering:            { label: 'Dağıtımda',       color: '#60a5fa', bg: '#eff6ff' },
};

function Pipeline({ pipeline }) {
  const total = (pipeline || []).reduce((s, p) => s + p.count, 0);
  if (total === 0) return <p className="text-sm text-gray-400 text-center py-6">Aktif sipariş yok</p>;
  return (
    <div className="space-y-3">
      {pipeline.map(row => {
        const cfg = PIPELINE_CFG[row.status] || { label: row.status, color: '#9ca3af', bg: '#f9fafb' };
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        return (
          <div key={row.status} className="flex items-center gap-3">
            <div className="w-36 shrink-0 text-right">
              <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
            </div>
            <div className="flex-1 rounded-full h-5 overflow-hidden" style={{ backgroundColor: cfg.bg }}>
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-700"
                style={{ width: `${Math.max(pct, 6)}%`, backgroundColor: cfg.color }}
              >
                <span className="text-[10px] text-white font-bold">{row.count}</span>
              </div>
            </div>
            <div className="w-10 text-right text-xs text-gray-400 shrink-0">{pct}%</div>
          </div>
        );
      })}
      <div className="pt-1 text-right text-xs text-gray-400">
        Toplam aktif: <span className="font-semibold text-gray-600">{total}</span>
      </div>
    </div>
  );
}

// ─── Ürün barı ────────────────────────────────────────────────────────────────

// rawValue: bar genişliği için sayısal değer, displayValue: gösterilecek metin
function ProductBar({ rank, offerId, name, rawValue, displayValue, max, color, suffix = '' }) {
  const pct    = max > 0 && rawValue > 0 ? Math.min((rawValue / max) * 100, 100) : 0;
  const medals = ['🥇', '🥈', '🥉'];
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="w-5 text-center text-sm shrink-0">
        {medals[rank] ?? <span className="text-xs text-gray-400 font-bold">{rank + 1}</span>}
      </div>
      <div className="w-40 shrink-0">
        <div className="text-xs font-semibold text-gray-800 truncate" title={offerId}>{offerId}</div>
        {name && <div className="text-[10px] text-gray-400 truncate mt-0.5" title={name}>{name}</div>}
      </div>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="w-20 text-right text-xs font-bold text-gray-700 shrink-0">
        {displayValue}{suffix}
      </div>
    </div>
  );
}

// ─── Günlük Satış Detayı ──────────────────────────────────────────────────────

function DailySalesDetail({ dailyDetail }) {
  const [openDate, setOpenDate] = useState(null);

  // Günlere göre grupla
  const grouped = useMemo(() => {
    const map = new Map();
    for (const row of (dailyDetail || [])) {
      const key = row.date;
      if (!map.has(key)) map.set(key, { date_label: row.date_label, date: key, products: [], total_qty: 0, total_revenue: 0 });
      const day = map.get(key);
      day.products.push(row);
      day.total_qty     += row.qty;
      day.total_revenue += parseFloat(row.revenue) || 0;
    }
    return Array.from(map.values());
  }, [dailyDetail]);

  if (!grouped.length) {
    return <p className="text-sm text-gray-400 text-center py-6">Son 30 günde veri yok</p>;
  }

  // Hepsi kapalı başlar
  const active = openDate;

  return (
    <div className="space-y-2">
      {grouped.map(day => {
        const isOpen = active === day.date;
        return (
          <div key={day.date} className="border border-gray-100 rounded-xl overflow-hidden">
            {/* Gün başlığı */}
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              onClick={() => setOpenDate(isOpen ? null : day.date)}
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-bold text-gray-800">{day.date_label}</span>
                <span className="text-xs text-gray-500">{day.products.length} ürün çeşidi</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-bold text-indigo-600">{day.total_qty} adet</span>
                <span className="text-sm font-semibold text-emerald-600">{fmtUsd(day.total_revenue)}</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Ürün listesi */}
            {isOpen && (
              <div className="divide-y divide-gray-50">
                {day.products.map((p, i) => (
                  <div key={p.offer_id} className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-50 transition-colors">
                    <span className="text-xs text-gray-300 w-5 text-right shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-700 truncate">{p.offer_id}</div>
                      <div className="text-[10px] text-gray-400 truncate mt-0.5">{p.name}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-gray-800">{p.qty} <span className="text-xs font-normal text-gray-400">adet</span></div>
                      <div className="text-[11px] text-gray-400">{fmtUsd(p.revenue)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

const TREND_LABEL = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };
const PERIODS = [
  { value: 30,  label: '30 Gün' },
  { value: 90,  label: '3 Ay'   },
  { value: 180, label: '6 Ay'   },
];

export default function Analytics() {
  const [period, setPeriod]     = useState(30);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [trendKey, setTrendKey] = useState('revenue');

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.getAnalytics(period)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  if (error)   return <div className="p-8 text-red-500 text-sm">{error}</div>;

  const { summary, trend, trendLabel, monthly, topProducts, cancelStats, cancelByProduct, pipeline, byDayOfWeek, dailyDetail } = data;

  const avgOrder    = summary.total_orders > 0 ? parseFloat(summary.total_revenue) / summary.total_orders : 0;
  const cancelRate  = cancelStats.total > 0 ? ((cancelStats.cancelled / cancelStats.total) * 100).toFixed(1) : 0;
  const successRate = (100 - parseFloat(cancelRate)).toFixed(1);

  const maxQty = Math.max(...(topProducts.map(p => p.total_qty)), 1);
  const maxRev = Math.max(...(topProducts.map(p => parseFloat(p.total_revenue))), 1);

  const lastTwo    = (monthly || []).slice(-2);
  const monthDelta = lastTwo.length === 2 && parseFloat(lastTwo[0].order_count) > 0
    ? Math.round(((lastTwo[1].order_count - lastTwo[0].order_count) / lastTwo[0].order_count) * 100)
    : null;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">

      {/* ── Başlık + Period ───────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Satış Analizi</h1>
          <p className="text-sm text-gray-400 mt-0.5">Ozon sipariş ve ciro trendleri</p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === p.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard icon="📦" label="Toplam Sipariş"  value={fmtNum(summary.total_orders)}         sub={`Son ${period} gün`}    accent="#6366f1" delta={monthDelta} />
        <KpiCard icon="💵" label="Toplam Ciro"      value={fmtUsd(summary.total_revenue)}         sub="İptal hariç"            accent="#059669" />
        <KpiCard icon="🧾" label="Ortalama Sepet"   value={fmtUsd(avgOrder)}                      sub="Sipariş başına"         accent="#7c3aed" />
        <KpiCard icon="✅" label="Teslim Edildi"    value={fmtNum(summary.delivered)}             sub={`Son ${period} gün`}   accent="#10b981" />
        <KpiCard icon="🎯" label="Başarı Oranı"     value={`%${successRate}`}                     sub={`${cancelStats.cancelled} iptal`} accent={parseFloat(cancelRate) > 10 ? '#ef4444' : '#f59e0b'} />
      </div>

      {/* ── Ana Trend ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-semibold text-gray-900">{TREND_LABEL[trendLabel]} Trend</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {trendLabel === 'daily' ? 'Son 30 gün, günlük' : trendLabel === 'weekly' ? '90 gün, haftalık' : '6 ay, aylık'} — iptal hariç
            </p>
          </div>
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setTrendKey('revenue')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${trendKey === 'revenue' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500'}`}
            >
              💵 Ciro
            </button>
            <button
              onClick={() => setTrendKey('order_count')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${trendKey === 'order_count' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500'}`}
            >
              📦 Sipariş
            </button>
          </div>
        </div>
        {(trend || []).length > 1 ? (
          <>
            <AreaChart
              data={trend}
              valueKey={trendKey}
              color={trendKey === 'revenue' ? '#6366f1' : '#10b981'}
              height={160}
              formatTooltip={trendKey === 'revenue' ? fmtUsd : v => `${fmtNum(v)} sipariş`}
            />
            <XLabels data={trend} maxLabels={period === 30 ? 8 : 7} />
          </>
        ) : (
          <div className="h-40 flex items-center justify-center text-gray-300 text-sm">Yeterli veri yok</div>
        )}
      </div>

      {/* ── Aylık + Haftanın Günleri ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {(monthly || []).length > 0 && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-0.5">Aylık Karşılaştırma</h2>
            <p className="text-xs text-gray-400 mb-5">Son 6 ay</p>
            <p className="text-xs text-gray-400 uppercase font-semibold mb-2">Ciro ($)</p>
            <ColumnChart data={monthly} valueKey="revenue" color="#6366f1" formatVal={fmtUsd} />
            <p className="text-xs text-gray-400 uppercase font-semibold mb-2 mt-5">Sipariş Adedi</p>
            <ColumnChart data={monthly} valueKey="order_count" color="#f59e0b" />
          </div>
        )}

        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-0.5">Hangi Günler Daha Yoğun?</h2>
          <p className="text-xs text-gray-400 mb-5">Son {period} günde sipariş dağılımı</p>
          <DayOfWeekChart data={byDayOfWeek || []} />

          <div className="mt-6 pt-4 border-t border-gray-50">
            <div className="flex justify-between text-xs text-gray-500 mb-1.5">
              <span>Tamamlanan</span><span>İptal</span>
            </div>
            <div className="h-3.5 bg-red-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                style={{ width: `${cancelStats.total > 0 ? (cancelStats.completed / cancelStats.total) * 100 : 0}%` }}
              />
            </div>
            <div className="flex justify-between text-xs mt-1.5">
              <span className="text-emerald-600 font-semibold">{fmtNum(cancelStats.completed)} tamamlanan</span>
              <span className="text-red-400 font-semibold">{fmtNum(cancelStats.cancelled)} iptal (%{cancelRate})</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Aktif Pipeline ─────────────────────────────────────────────── */}
      {(pipeline || []).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-0.5">Aktif Sipariş Durumu</h2>
          <p className="text-xs text-gray-400 mb-5">Şu an sistemdeki siparişler, aşamaya göre</p>
          <Pipeline pipeline={pipeline} />
        </div>
      )}

      {/* ── Günlük Satış Detayı ────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
        <h2 className="font-semibold text-gray-900 mb-0.5">Günlük Satış Detayı</h2>
        <p className="text-xs text-gray-400 mb-5">Son 30 gün — her gün hangi ürün kaç adet satıldı</p>
        <DailySalesDetail dailyDetail={dailyDetail || []} />
      </div>

      {/* ── En Çok Satanlar ────────────────────────────────────────────── */}
      {(topProducts || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-0.5">En Çok Satan (Adet)</h2>
            <p className="text-xs text-gray-400 mb-4">Son {period} gün</p>
            {topProducts.map((p, i) => (
              <ProductBar key={p.offer_id} rank={i} offerId={p.offer_id} name={p.name}
                rawValue={p.total_qty} displayValue={fmtNum(p.total_qty)} max={maxQty}
                color={['#f59e0b','#94a3b8','#b45309'][i] ?? '#6366f1'} suffix=" adet" />
            ))}
          </div>
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-0.5">En Çok Kazandıran (Ciro)</h2>
            <p className="text-xs text-gray-400 mb-4">Son {period} gün</p>
            {[...topProducts]
              .sort((a, b) => parseFloat(b.total_revenue) - parseFloat(a.total_revenue))
              .map((p, i) => (
                <ProductBar key={p.offer_id} rank={i} offerId={p.offer_id} name={p.name}
                  rawValue={parseFloat(p.total_revenue)} displayValue={fmtUsd(p.total_revenue)} max={maxRev}
                  color={['#10b981','#34d399','#6ee7b7'][i] ?? '#a7f3d0'} />
              ))}
          </div>
        </div>
      )}

      {/* ── İptal Analizi ─────────────────────────────────────────────────── */}
      {(cancelByProduct || []).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-0.5">İptal Analizi — Ürün Bazlı</h2>
          <p className="text-xs text-gray-400 mb-5">Son {period} gün — en az 1 iptali olan ürünler, iptal oranına göre sıralı</p>

          {/* Genel özet */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Toplam Sipariş', value: fmtNum(cancelStats?.total || 0),     color: 'text-gray-700',    bg: 'bg-gray-50' },
              { label: 'İptal Edilen',   value: fmtNum(cancelStats?.cancelled || 0), color: 'text-red-600',     bg: 'bg-red-50' },
              { label: 'İptal Oranı',    value: `%${cancelRate}`,
                color: parseFloat(cancelRate) > 10 ? 'text-red-600' : 'text-emerald-600',
                bg:    parseFloat(cancelRate) > 10 ? 'bg-red-50'    : 'bg-emerald-50' },
            ].map(c => (
              <div key={c.label} className={`${c.bg} rounded-xl p-3 text-center`}>
                <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
              </div>
            ))}
          </div>

          {/* Ürün tablosu */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase">#</th>
                  <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase">Ürün</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-20">Satılan</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-20">İptal</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase w-24">Oran</th>
                  <th className="py-2 w-36"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {cancelByProduct.map((p, i) => {
                  const rate = parseFloat(p.iptal_orani) || 0;
                  const barColor = rate >= 20 ? '#ef4444' : rate >= 10 ? '#f97316' : '#eab308';
                  return (
                    <tr key={p.offer_id} className="hover:bg-gray-50/60">
                      <td className="py-2.5 text-xs text-gray-300">{i + 1}</td>
                      <td className="py-2.5 pr-4">
                        <div className="font-semibold text-xs text-gray-800">{p.offer_id}</div>
                        <div className="text-[11px] text-gray-400 truncate max-w-[180px]">{p.name}</div>
                      </td>
                      <td className="py-2.5 text-right text-sm text-gray-600">{fmtNum(p.satilan)}</td>
                      <td className="py-2.5 text-right text-sm font-semibold text-red-500">{fmtNum(p.iptal)}</td>
                      <td className="py-2.5 text-right">
                        <span className={`text-sm font-bold ${rate >= 20 ? 'text-red-600' : rate >= 10 ? 'text-orange-500' : 'text-yellow-600'}`}>
                          %{rate.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-2.5 pl-3">
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className="h-2 rounded-full" style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: barColor }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}
